import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NEXT_STEP_META } from '../../constants';
import { PaymentsService } from '../../api/PaymentsAPI';
import { getNextStep } from '../../utils';
import s from '../../styles/dashboard.module.css';

// Which extra field each handling behaviour asks for. `links_itself` needs
// nothing — Square sends that payment through on its own.
const DETAIL_FOR_HANDLING = {
  receivable:      { key: 'invoiceNumber',          label: 'Invoice / account number', required: false },
  manual:          { key: 'eTransferConfirmPerson', label: 'Confirmed by',             required: false },
  needs_reason:    { key: 'reason',                 label: 'Reason',                   required: true  },
  action_required: { key: 'reason',                 label: 'What happened?',           required: true  },
};

// Walks an appointment through its next checklist step: optional comment,
// payment method, and required before/after photos.
export function NextStepModal({ appt, onClose, onConfirm }) {
  const next = getNextStep(appt);
  const [comment, setComment]       = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDetail, setPaymentDetail] = useState('');
  const [files, setFiles]           = useState([]);
  const [previews, setPreviews]     = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const fileRef = useRef(null);

  // The picker's options live in the `paymentMethods` table, so adding or
  // retiring a method is a data change, not a release.
  const { data: methodsData } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => PaymentsService.methods(),
    staleTime: 60 * 60 * 1000,
  });
  const methods = methodsData?.methods || [];

  useEffect(() => {
    const urls = files.map(f => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [files]);

  if (!next) {
    return (
      <div className={s.qrOverlay} onClick={onClose}>
        <div className={s.nextStepPanel} onClick={e => e.stopPropagation()}>
          <div className={s.nextStepEyebrow}>All Done</div>
          <div className={s.nextStepPrompt}>This appointment has completed every step.</div>
          <div className={s.nextStepActions}>
            <button className={s.nextStepCancel} onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const meta = NEXT_STEP_META[next.step.toLowerCase()]
    || { title: next.step, prompt: `Mark "${next.step}" as done?`, requiredPhotos: 0, phase: 'photo', confirmLabel: 'Confirm' };
  const required     = meta.requiredPhotos || 0;
  const enoughPhotos = files.length >= required;
  const isPayment    = next.step.toLowerCase() === 'payment';
  const chosen       = methods.find(m => m.code === paymentMethod) || null;
  const detailField  = chosen ? DETAIL_FOR_HANDLING[chosen.method] : null;
  const detailMissing = !!detailField?.required && !paymentDetail.trim();
  const canConfirm   = enoughPhotos && (!isPayment || (!!paymentMethod && !detailMissing)) && !submitting;

  const handlePick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) setFiles(prev => [...prev, ...picked]);
    e.target.value = '';
  };
  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const handleYes = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setError('');
    try {
      await onConfirm({
        appt, step: next.step, idx: next.idx, comment, files, phase: meta.phase,
        paymentMethod: isPayment ? paymentMethod : undefined,
        paymentDetails: isPayment && detailField && paymentDetail.trim()
          ? { [detailField.key]: paymentDetail.trim() }
          : undefined,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Something went wrong');
      setSubmitting(false);
    }
  };

  return (
    <div className={s.qrOverlay} onClick={submitting ? undefined : onClose}>
      <div className={s.nextStepPanel} onClick={e => e.stopPropagation()}>
        <div className={s.nextStepEyebrow}>{meta.title}</div>
        <div className={s.nextStepCar}>{appt.car}</div>
        <div className={s.nextStepPrompt}>{meta.prompt}</div>

        <textarea
          className={s.nextStepTextarea}
          placeholder="Add a comment (optional)…"
          value={comment}
          onChange={e => setComment(e.target.value)}
          rows={3}
        />

        {isPayment && (
          <>
            <select
              className={s.nextStepTextarea}
              value={paymentMethod}
              onChange={e => { setPaymentMethod(e.target.value); setPaymentDetail(''); }}
              disabled={submitting}
              style={{ height: 40 }}
            >
              <option value="" disabled>Select payment method…</option>
              {methods.map(m => (
                <option key={m.code} value={m.code}>{m.title}</option>
              ))}
            </select>

            {chosen?.description && (
              <div className={s.nextStepHint}>{chosen.description}</div>
            )}

            {detailField && (
              <input
                className={s.nextStepTextarea}
                type="text"
                placeholder={detailField.required ? `${detailField.label} (required)` : `${detailField.label} (optional)`}
                value={paymentDetail}
                onChange={e => setPaymentDetail(e.target.value)}
                disabled={submitting}
                style={{ height: 40 }}
              />
            )}
          </>
        )}

        {required > 0 && (
          <>
            <div className={s.nextStepPhotoHead}>
              <span className={s.nextStepPhotoLabel}>Photos · {meta.phase}</span>
              <span className={enoughPhotos ? s.nextStepPhotoOk : s.nextStepPhotoNeed}>
                {files.length} / {required} required
              </span>
            </div>
            <div className={s.nextStepThumbs}>
              {previews.map((url, i) => (
                <div key={i} className={s.nextStepThumbWrap}>
                  <img src={url} alt="" className={s.nextStepThumb} />
                  <button className={s.nextStepThumbX} onClick={() => removeFile(i)} disabled={submitting}>✕</button>
                </div>
              ))}
              <button className={s.nextStepAddPhoto} onClick={() => fileRef.current?.click()} disabled={submitting}>+ Add</button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handlePick} />
          </>
        )}

        {error && <div className={s.nextStepError}>{error}</div>}

        <div className={s.nextStepActions}>
          <button className={s.nextStepCancel} onClick={onClose} disabled={submitting}>Cancel</button>
          <button className={s.nextStepConfirm} onClick={handleYes} disabled={!canConfirm}>
            {submitting ? 'Saving…' : (meta.confirmLabel || 'Yes')}
          </button>
        </div>
        {required > 0 && !enoughPhotos && (
          <div className={s.nextStepHint}>Add at least {required} photos to continue.</div>
        )}
        {isPayment && !paymentMethod && (
          <div className={s.nextStepHint}>Select a payment method to continue.</div>
        )}
        {isPayment && detailMissing && (
          <div className={s.nextStepHint}>{detailField.label} is required for {chosen.title}.</div>
        )}
      </div>
    </div>
  );
}
